import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";

import StatsPanel from "@/pages/admin/StatsPanel";
import UsersPanel from "@/pages/admin/UsersPanel";
import BlogPanel from "@/pages/admin/BlogPanel";
import ShopPanel from "@/pages/admin/ShopPanel";
import AdsPanel from "@/pages/admin/AdsPanel";
import ImageryPanel from "@/pages/admin/ImageryPanel";
import GiftCodesPanel from "@/pages/admin/GiftCodesPanel";
import SiteConfigPanel from "@/pages/admin/SiteConfigPanel";
import SchedulerPanel from "@/pages/admin/SchedulerPanel";
import LifetimeGiftsPanel from "@/pages/admin/LifetimeGiftsPanel";
import PageMeta from "@/components/PageMeta";
import HomeContentPanel from "@/pages/admin/HomeContentPanel";
import ReportsPanel from "@/pages/admin/ReportsPanel";
import RecipesPanel from "@/pages/admin/RecipesPanel";
import CookbooksPanel from "@/pages/admin/CookbooksPanel";
import RevenuePanel from "@/pages/admin/RevenuePanel";
import SeasonalThemesPanel from "@/pages/admin/SeasonalThemesPanel";
import ErrorsPanel from "@/pages/admin/ErrorsPanel";
import ReferralsPanel from "@/pages/admin/ReferralsPanel";
import HistoricalPanel from "@/pages/admin/HistoricalPanel";
import HistoricalSourcesPanel from "@/pages/admin/HistoricalSourcesPanel";
import BulkSeoPanel from "@/pages/admin/BulkSeoPanel";
import SynonymsPanel from "@/pages/admin/SynonymsPanel";
import TagsPanel from "@/pages/admin/TagsPanel";
import DuplicatesPanel from "@/pages/admin/DuplicatesPanel";
import FAQsPanel from "@/pages/admin/FAQsPanel";
import EmailAnalyticsPanel from "@/pages/admin/EmailAnalyticsPanel";
import KitchenHelpPanel from "@/pages/admin/KitchenHelpPanel";
import WorldRecipesPanel from "@/pages/admin/WorldRecipesPanel";
import FamilyRecipesPanel from "@/pages/admin/FamilyRecipesPanel";
import AisleCorrectionsPanel from "@/pages/admin/AisleCorrectionsPanel";
import FamilyPlansPanel from "@/pages/admin/FamilyPlansPanel";
import VoiceAnalyticsPanel from "@/pages/admin/VoiceAnalyticsPanel";

const TABS = [
  { key: "stats", label: "STATS" },
  { key: "users", label: "USERS" },
  { key: "revenue", label: "SUBSCRIPTIONS & REVENUE" },
  { key: "seasonal", label: "SEASONAL THEMES" },
  { key: "recipes", label: "RECIPES" },
  { key: "cookbooks", label: "COOKBOOKS" },
  { key: "reports", label: "REPORTS" },
  { key: "home", label: "SITE COPY" },
  { key: "faqs", label: "FAQS" },
  { key: "kitchen-help", label: "KITCHEN HELP" },
  { key: "world-recipes", label: "WORLD RECIPES" },
  { key: "family-recipes", label: "FAMILY RECIPES" },
  { key: "aisle-corrections", label: "AISLE FIXES" },
  { key: "blog", label: "BLOG" },
  { key: "shop", label: "SHOP" },
  { key: "ads", label: "ADS" },
  { key: "imagery", label: "IMAGERY" },
  { key: "gift", label: "GIFT CODES" },
  { key: "gifts", label: "LIFETIME GIFTS" },
  { key: "family-plans", label: "FAMILY PLANS" },
  { key: "voice-analytics", label: "VOICE ANALYTICS" },
  { key: "referrals", label: "REFERRALS" },
  { key: "historical", label: "HISTORICAL" },
  { key: "historical-sources", label: "HISTORICAL SOURCES" },
  { key: "bulkseo", label: "BULK SEO" },
  { key: "synonyms", label: "SYNONYMS" },
  { key: "tags", label: "TAGS" },
  { key: "duplicates", label: "DUPLICATES" },
  { key: "settings", label: "SITE CONFIG" },
  { key: "email-analytics", label: "EMAIL STATS" },
  { key: "scheduler", label: "SCHEDULER" },
  { key: "errors", label: "ERROR LOG" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");

  // Redirect anonymous visitors to login (with return-path)
  useEffect(() => {
    if (!loading && !user) nav("/login?next=/admin", { replace: true });
  }, [loading, user, nav]);

  if (loading || !user) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <p className="overline mb-3">Admin</p>
        <h1 className="font-display font-black text-3xl text-rf-ink mb-3">Sign in required</h1>
        <p className="text-rf-ink-soft mb-6">Redirecting you to sign in…</p>
        <Link to="/login?next=/admin" data-testid="admin-login-link" className="btn-amber">
          Sign in
        </Link>
      </div>
    );
  }

  if (user.role !== "Admin" && user.role !== "SuperAdmin") {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <p className="overline mb-3">Admin</p>
        <h1 className="font-display font-black text-3xl text-rf-ink mb-3">Admin access required</h1>
        <p className="text-rf-ink-soft mb-6">
          You&apos;re signed in as <b>{user.email}</b> but this account doesn&apos;t have admin permissions.
        </p>
        <Link to="/dashboard" className="btn-outline-ink">Back to dashboard</Link>
      </div>
    );
  }

  const visibleTabs = TABS.filter(
    (t) => !(["gifts", "settings"].includes(t.key) && user.role !== "SuperAdmin"),
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <PageMeta title="Admin" noindex />
      <p className="overline mb-2">Admin</p>
      <h1 className="font-display text-4xl md:text-5xl font-black text-rf-ink mb-8">Control panel</h1>

      <div className="flex flex-wrap gap-2 mb-10">
        {visibleTabs.map(({ key, label }) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-full text-sm font-bold ${tab === key ? "bg-rf-ink text-rf-cream" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "stats" && <StatsPanel />}
      {tab === "users" && <UsersPanel />}
      {tab === "recipes" && <RecipesPanel />}
      {tab === "cookbooks" && <CookbooksPanel />}
      {tab === "revenue" && <RevenuePanel />}
      {tab === "seasonal" && <SeasonalThemesPanel />}
      {tab === "reports" && <ReportsPanel />}
      {tab === "home" && <HomeContentPanel />}
      {tab === "faqs" && <FAQsPanel />}
      {tab === "kitchen-help" && <KitchenHelpPanel />}
      {tab === "world-recipes" && <WorldRecipesPanel />}
      {tab === "family-recipes" && <FamilyRecipesPanel />}
      {tab === "aisle-corrections" && <AisleCorrectionsPanel />}
      {tab === "blog" && <BlogPanel />}
      {tab === "shop" && <ShopPanel />}
      {tab === "ads" && <AdsPanel />}
      {tab === "imagery" && <ImageryPanel />}
      {tab === "gift" && <GiftCodesPanel />}
      {tab === "gifts" && user.role === "SuperAdmin" && <LifetimeGiftsPanel />}
      {tab === "family-plans" && <FamilyPlansPanel />}
      {tab === "voice-analytics" && <VoiceAnalyticsPanel />}
      {tab === "settings" && user.role === "SuperAdmin" && <SiteConfigPanel />}
      {tab === "email-analytics" && <EmailAnalyticsPanel />}
      {tab === "scheduler" && <SchedulerPanel />}
      {tab === "errors" && <ErrorsPanel />}
      {tab === "referrals" && <ReferralsPanel />}
      {tab === "historical" && <HistoricalPanel />}
      {tab === "historical-sources" && <HistoricalSourcesPanel />}
      {tab === "bulkseo" && <BulkSeoPanel />}
      {tab === "synonyms" && <SynonymsPanel />}
      {tab === "tags" && <TagsPanel />}
      {tab === "duplicates" && <DuplicatesPanel />}
    </div>
  );
}
